import type { HTMLAttributes } from 'react';
import { forwardRef, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { AngleBoldDown16 } from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { Typography } from '@elfsight/widget-ui';
import { useLingui } from '@lingui/react/macro';
import useMergedRef from '@react-hook/merged-ref';
import type { BusinessHoursProps } from '../../common/business-hours-types';
import { useBusinessHoursProps } from '../business-hours-props-context';
import { useCurrentStatus } from '../current-status-context';
import { SchedulePopout } from '../schedule/schedule-popout';
import {
  useRelativeDateFormatter,
  useRelativeTimeFormatter,
  useTimeFormatter
} from '../utils/date-time-format';

const lowercaseDateLanguages = ['nl', 'ru'];

function wrapTimeIntoHTML(time: string) {
  return `<time class="elfsight-time">${time}</time>`;
}

function wrapDateIntoHTML(date: string) {
  return `<span class="elfsight-date">${date}</span>`;
}

export const StatusIndicator = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & BusinessHoursProps
>(function (
  {
    openStatusColor,
    closedSoonStatusColor,
    closedStatusColor,
    textStyle,
    textColor,
    alignment,
    mode,
    className
  },
  forwardRef
) {
  const { language } = useBusinessHoursProps();
  const { t } = useLingui();
  const dateTextTransform = lowercaseDateLanguages.includes(language?.language)
    ? 'lowercase'
    : undefined;

  const containerRef = useRef<HTMLDivElement | null>(null);

  const mergedRef = useMergedRef(containerRef, forwardRef);

  const formatRelativeDate = useRelativeDateFormatter();
  const formatRelativeTime = useRelativeTimeFormatter();
  const formatTime = useTimeFormatter();

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const { isCurrentlyOpen, nextChangeIn, nextChangeWithinTwoHours } =
    useCurrentStatus();

  type RenderProps = { title: string; caption?: string; color: string };

  const withDropdownSchedule = mode === 'statusWithHours';

  const render = ({ title, caption, color }: RenderProps) => (
    <Container
      ref={mergedRef}
      _intractable={withDropdownSchedule}
      _alignment={alignment}
      onClick={() => setScheduleOpen(!scheduleOpen)}
      className={className}
    >
      <Title color={color} textStyle={textStyle}>
        <span dangerouslySetInnerHTML={{ __html: title }} />
        {withDropdownSchedule && (
          <TriggerIcon
            _active={scheduleOpen}
            component={AngleBoldDown16}
            size={12}
            fill="currentColor"
          />
        )}
      </Title>
      {caption && (
        <Caption
          color={getColorWithAlpha(textColor, 0.5)}
          textStyle={textStyle}
          _dateTextTransform={dateTextTransform}
        >
          <span dangerouslySetInnerHTML={{ __html: caption }} />
        </Caption>
      )}
      {withDropdownSchedule && (
        <SchedulePopout
          show={scheduleOpen}
          anchorElement={containerRef.current}
          onCloseRequest={() => setScheduleOpen(false)}
        />
      )}
    </Container>
  );

  const renderClosed = (caption: string) =>
    render({ color: closedStatusColor, title: t`Closed`, caption });

  if (isCurrentlyOpen) {
    if (!nextChangeIn) {
      return render({
        color: openStatusColor,
        title: t`Open`
      });
    }

    if (nextChangeWithinTwoHours) {
      const time = formatRelativeTime(nextChangeIn);
      const convertedTime = wrapTimeIntoHTML(time);
      return render({
        color: closedSoonStatusColor,
        title: t`Closes in ${convertedTime}`
      });
    }

    const time = formatTime(nextChangeIn);
    const convertedTime = wrapTimeIntoHTML(time);
    return render({
      color: openStatusColor,
      title: t`Open until ${convertedTime}`
    });
  } else {
    if (!nextChangeIn) {
      return renderClosed('');
    }

    if (nextChangeWithinTwoHours) {
      const time = formatRelativeTime(nextChangeIn);
      const convertedTime = wrapTimeIntoHTML(time);
      return renderClosed(t`Opens in ${convertedTime}`);
    }

    const date = formatRelativeDate(nextChangeIn);
    const time = formatTime(nextChangeIn);
    const convertedTime = wrapTimeIntoHTML(time);
    const convertedDate = wrapDateIntoHTML(date);
    return renderClosed(t`Opens ${convertedDate} at ${convertedTime}`);
  }
});

const Container = styled.div<{
  _intractable: boolean;
  _alignment: string;
}>`
  display: inline-flex;
  position: relative;
  flex-direction: row;
  justify-content: ${({ _alignment }) => _alignment};
  flex-wrap: wrap;
  gap: 4px;
  cursor: ${({ _intractable }) => (_intractable ? 'pointer' : 'inherit')};
`;

const Title = styled(Typography)`
  display: inline;
`;
const Caption = styled(Typography)<{ _dateTextTransform?: string }>`
  display: block;

  ${({ _dateTextTransform }) => css`
    & .elfsight-date {
      text-transform: ${_dateTextTransform};
    }
  `}
`;

const TriggerIcon = styled(Icon)<{ _active: boolean }>`
  transform: rotate(${({ _active }) => (_active ? '180deg' : '0')});
  display: inline-flex;
  margin-left: 4px;
`;
